import {
  FIREBASE_AUTH,
  FIREBASE_DB,
  USE_LOCAL_DEMO_AUTH,
} from "../../firebaseConfig";
import { updateProfile } from "firebase/auth";
import {
  getFirestore,
  doc,
  updateDoc,
  query,
  collection,
  where,
  getDocs,
  getDoc,
  deleteDoc,
  setDoc,
} from "firebase/firestore";
import { saveMediaToStorage } from "./utils";
import { SearchUser, User } from "../../types";
import { demoUser } from "../demo/localDemo";

const USER_CACHE_PREFIX = "kirkwat-user-cache:";

function readCachedUser(id: string): User | null {
  if (typeof window === "undefined") return null;
  try {
    return JSON.parse(window.localStorage.getItem(`${USER_CACHE_PREFIX}${id}`) || "null");
  } catch {
    return null;
  }
}

function cacheUser(user: User) {
  if (typeof window === "undefined" || !user?.uid) return;
  try {
    window.localStorage.setItem(`${USER_CACHE_PREFIX}${user.uid}`, JSON.stringify(user));
  } catch {}
}

export const saveUserProfileImage = (image: string) =>
  new Promise<void>(async (resolve, reject) => {
    try {
      if (FIREBASE_AUTH.currentUser) {
        const downloadURL = await saveMediaToStorage(
          image,
          `profileImage/${FIREBASE_AUTH.currentUser.uid}`,
        );

        const db = getFirestore();
        const userDoc = doc(db, "user", FIREBASE_AUTH.currentUser.uid);

        await updateDoc(userDoc, {
          photoURL: downloadURL,
        });

        // Also update the user profile in Firebase Auth if needed
        await updateProfile(FIREBASE_AUTH.currentUser, {
          photoURL: downloadURL,
        });

        resolve();
      }
    } catch (error) {
      console.error("Failed to save user profile image: ", error);
      reject(error);
    }
  });

export const saveUserField = (field: string, value: string) =>
  new Promise<void>(async (resolve, reject) => {
    try {
      if (FIREBASE_AUTH.currentUser) {
        const userDoc = doc(FIREBASE_DB, "user", FIREBASE_AUTH.currentUser.uid);

        let obj: { [key: string]: string } = {};
        obj[field] = value;

        await updateDoc(userDoc, obj);
        resolve();
      }
    } catch (error) {
      console.error("Failed to save user field: ", error);
      reject(error);
    }
  });

export const queryUsersByEmail = (email: string): Promise<SearchUser[]> => {
  return new Promise(async (resolve, reject) => {
    if (USE_LOCAL_DEMO_AUTH) {
      resolve(
        demoUser.email.includes(email)
          ? [{ ...demoUser, id: demoUser.uid }]
          : [],
      );
      return;
    }

    try {
      if (email === "") {
        resolve([]);
        return;
      }

      const q = query(
        collection(FIREBASE_DB, "user"),
        where("email", ">=", email),
        where("email", "<=", email + "\uf8ff"),
      );

      const querySnapshot = await getDocs(q);
      const users = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        const id = doc.id;
        return { id, ...data } as SearchUser;
      });

      resolve(users);
    } catch (error) {
      console.error("Failed to query users: ", error);
      reject(error);
    }
  });
};

/**
 * fetches the doc corresponding to the id of a user.
 *
 * @param {String} id of the user we want to fetch
 * @returns {Promise<User>} user object if successful.
 */
export const getUserById = async (id: string): Promise<User | null> => {
  if (USE_LOCAL_DEMO_AUTH) {
    if (id === demoUser.uid) cacheUser(demoUser);
    return id === demoUser.uid ? demoUser : null;
  }

  try {
    const docSnapshot = await getDoc(doc(FIREBASE_DB, "user", id));
    if (docSnapshot.exists()) {
      const user = docSnapshot.data() as User;
      cacheUser(user);
      return user;
    } else {
      return readCachedUser(id);
    }
  } catch (error) {
    const cached = readCachedUser(id);
    if (cached) {
      console.warn("Using cached user profile fallback:", error);
      return cached;
    }
    throw new Error(String(error));
  }
};

/**
 * Checks if a user is following another by seeing if a follow doc exists.
 *
 * @param {String} userId of the user we want to see if it's following another
 * @param {String} otherUserId the id of the user that we want to check if it's being followed by another.
 * @returns {Boolean} if true means the user is indeed following the other User
 */
export const getIsFollowing = async (userId: string, otherUserId: string) => {
  if (USE_LOCAL_DEMO_AUTH) {
    return false;
  }

  const userDocRef = doc(FIREBASE_DB, "user", userId, "following", otherUserId);

  try {
    const docSnap = await getDoc(userDocRef);

    if (docSnap.exists()) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Error checking following status: ", error);
    return false;
  }
};

/**
 * Changes the follow state of two users depending on the current
 * follow state.
 *
 * @param {Object} props object containing the relevant info
 * @param {Boolean} isFollowing current follow state
 * @param {String} otherUserId the id of the user that we want to check if it's being followed by another.
 * @returns
 */
export const changeFollowState = async ({
  otherUserId,
  isFollowing,
}: {
  otherUserId: string;
  isFollowing: boolean;
}) => {
  if (USE_LOCAL_DEMO_AUTH) {
    return true;
  }

  const currentUserUid = FIREBASE_AUTH.currentUser
    ? FIREBASE_AUTH.currentUser.uid
    : null;

  if (!currentUserUid) {
    console.error("No current user");
    return false;
  }

  const followingDocRef = doc(
    FIREBASE_DB,
    "user",
    currentUserUid,
    "following",
    otherUserId,
  );

  try {
    if (isFollowing) {
      await deleteDoc(followingDocRef);
      return true;
    } else {
      await setDoc(followingDocRef, {});
      return true;
    }
  } catch (error) {
    console.error("Error changing follow state: ", error);
    return false;
  }
};
